import { decodePng, cropPixels } from './png-codec.js';
import type { Page, Locator } from '@playwright/test';
import type { RgbTuple, RGBA, Rect, Subrect, PixelData } from './types.js';

// Subrect is now exported from types.ts for consistency

function toHex(n: number) {
  const v = Math.max(0, Math.min(255, Math.round(n)));
  return v.toString(16).padStart(2, '0');
}

function parseHexColor(hex: string): RgbTuple | null {
  let value = hex.trim().toLowerCase();
  if (value.startsWith('#')) value = value.slice(1);
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }
  if (value.length !== 6 || /[^0-9a-f]/i.test(value)) return null;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return [r, g, b];
}

function parseColorToRgb(color: string | null | undefined): RgbTuple | null {
  if (!color) return null;
  const directHex = parseHexColor(color);
  if (directHex) return directHex;
  const fromRgb = rgbStringToHex(color);
  if (!fromRgb) return null;
  return parseHexColor(fromRgb);
}

// Parse rgb/rgba string to hex; returns null if cannot parse
export function rgbStringToHex(rgb: string | null | undefined): string | null {
  if (!rgb) return null;
  const m = rgb.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
  const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
  const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
}

/**
 * Get computed background color of the first element matching selector as hex.
 * Throws if the element is not found or the color cannot be determined.
 */
export async function getBackgroundColorHex(page: Page, selector: string): Promise<string> {
  const hex: string | null = await page.evaluate((sel: string) => {
    function toHex(n: number) { return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0'); }
    function rgbToHex(rgb: string | null | undefined): string | null {
      if (!rgb) return null;
      const m = rgb.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
      if (!m) return null;
      const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
      const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
      const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
    }
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) return null;
    const color = getComputedStyle(el).backgroundColor;
    return rgbToHex(color);
  }, selector);

  if (!hex) throw new Error(`getBackgroundColorHex: Element '${selector}' not found or no background color`);
  return hex;
}

/**
 * Get the average color (RGB) of the element screenshot as hex.
 * Optional subrect crops the element's screenshot before averaging.
 */
export async function getAverageColorHex(page: Page, selector: string, subrect?: Subrect): Promise<string> {
  const locator = page.locator(selector);
  const count = await locator.count();
  if (count < 1) throw new Error(`getAverageColorHex: Element '${selector}' not found`);
  const buffer: Buffer = await locator.screenshot();
  const decoded = decodePng(buffer);
  const width = decoded.width;
  const height = decoded.height;
  const data = decoded.data;

  const region = subrect
    ? {
        x: Math.max(0, Math.min(width - 1, Math.floor(subrect.x))),
        y: Math.max(0, Math.min(height - 1, Math.floor(subrect.y))),
        width: Math.max(1, Math.min(width - Math.max(0, Math.min(width - 1, Math.floor(subrect.x))), Math.floor(subrect.width))),
        height: Math.max(1, Math.min(height - Math.max(0, Math.min(height - 1, Math.floor(subrect.y))), Math.floor(subrect.height))),
      }
    : { x: 0, y: 0, width, height };

  let rSum = 0, gSum = 0, bSum = 0, n = 0;
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) {
      const idx = (y * width + x) * 4;
      const a = data[idx + 3];
      if (a === 0) continue; // skip fully transparent
      rSum += data[idx];
      gSum += data[idx + 1];
      bSum += data[idx + 2];
      n++;
    }
  }
  if (n === 0) throw new Error(`getAverageColorHex: No non-transparent pixels found for '${selector}'`);
  const r = Math.round(rSum / n);
  const g = Math.round(gSum / n);
  const b = Math.round(bSum / n);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
}

/**
 * Compare two colors (hex or rgb string) and determine if they are within the provided similarity threshold.
 * Threshold defaults to 0.98, representing 98% similarity based on normalized Euclidean distance in RGB space.
 */
export function colorIsWithinThreshold(mainColor: string, testColor: string, threshold = 0.98): boolean {
  if (threshold < 0 || threshold > 1) {
    throw new Error(`colorIsWithinThreshold: Threshold must be within [0,1]; received ${threshold}`);
  }
  const base = parseColorToRgb(mainColor);
  const sample = parseColorToRgb(testColor);
  if (!base || !sample) {
    throw new Error(`colorIsWithinThreshold: Unable to parse colors '${mainColor}' and '${testColor}'`);
  }
  const deltaR = base[0] - sample[0];
  const deltaG = base[1] - sample[1];
  const deltaB = base[2] - sample[2];
  const distance = Math.sqrt(deltaR * deltaR + deltaG * deltaG + deltaB * deltaB);
  const maxDistance = Math.sqrt(255 * 255 * 3);
  const similarity = 1 - distance / maxDistance;
  return similarity >= threshold;
}

/**
 * Convenience factory to match the desired call style without passing page every time.
 */
export function createDomColorHelpers(page: Page) {
  return {
    getBackgroundColor: (selector: string) => getBackgroundColorHex(page, selector),
    getAverageColor: (selector: string, subrect?: Subrect) => getAverageColorHex(page, selector, subrect),
    colorIsWithinThreshold: (mainColor: string, testColor: string, threshold?: number) =>
      colorIsWithinThreshold(mainColor, testColor, threshold ?? 0.98),
  };
}

// Back-compat short aliases
export const getBackgroundColor = getBackgroundColorHex;
export const getAverageColor = getAverageColorHex;
export const colorsWithinThreshold = colorIsWithinThreshold;



// RGB/RGBA types now imported from shared types.ts

export interface ColorMatchOptions {
  /** Whether to include alpha channel in comparison (default: false) */
  includeAlpha?: boolean;
  rect?: Rect;
}

/**
 * Converts an RGBA value (array or object) into a normalized array form.
 */
function rgbaToArray(input: RGBA): [number, number, number, number?] {
  if (Array.isArray(input)) {
    return input;
  } else {
    const { r, g, b, a } = input;
    return a !== undefined ? [r, g, b, a] : [r, g, b];
  }
}


/**
 * Calculates the fraction (0–1) of pixels in a canvas or PNG
 * that exactly match a specified RGB(A) color.
 *
 * Supports:
 *  - (page, selector, target, options)
 *  - (locator, target, options)
 *  - (png | buffer, target, options)
 *
 * If a `rect` option is provided, only that region is analyzed.
 */
export async function fractionOfColor(
  page: Page,
  selector: string,
  target: RGBA,
  options?: ColorMatchOptions
): Promise<number>;
export async function fractionOfColor(
  locator: Locator,
  target: RGBA,
  options?: ColorMatchOptions
): Promise<number>;
export async function fractionOfColor(
  pngOrBuffer: PixelData | Buffer | Uint8Array,
  target: RGBA,
  options?: ColorMatchOptions
): Promise<number>;

/**
 * Implementation
 */
export async function fractionOfColor(
  input: Page | Locator | PixelData | Buffer | Uint8Array,
  selectorOrTarget: string | RGBA,
  maybeTargetOrOptions?: RGBA | ColorMatchOptions,
  maybeOptions?: ColorMatchOptions
): Promise<number> {
  let pixels: PixelData;
  let target: RGBA;
  let options: ColorMatchOptions | undefined;

  // Determine which overload pattern was used
  if (Buffer.isBuffer(input) || (input instanceof Uint8Array && !('width' in input))) {
    // Case 3a: Buffer or raw Uint8Array — decode PNG
    pixels = decodePng(input);
    target = selectorOrTarget as RGBA;
    options = maybeTargetOrOptions as ColorMatchOptions;
  } else if ('width' in input && 'height' in input && 'data' in input) {
    // Case 3b: PixelData
    pixels = input as PixelData;
    target = selectorOrTarget as RGBA;
    options = maybeTargetOrOptions as ColorMatchOptions;
  } else if (typeof selectorOrTarget === 'string') {
    // Case 1: Page + selector
    const page = input as Page;
    const buffer = await page.locator(selectorOrTarget).screenshot();
    pixels = decodePng(buffer);
    target = maybeTargetOrOptions as RGBA;
    options = maybeOptions;
  } else {
    // Case 2: Locator
    const locator = input as Locator;
    const buffer = await locator.screenshot();
    pixels = decodePng(buffer);
    target = selectorOrTarget as RGBA;
    options = maybeTargetOrOptions as ColorMatchOptions;
  }

  // Crop region if specified
  if (options?.rect) {
    const { x, y, width, height } = options.rect;
    if (
      x >= 0 &&
      y >= 0 &&
      width > 0 &&
      height > 0 &&
      x + width <= pixels.width &&
      y + height <= pixels.height
    ) {
      pixels = cropPixels(pixels, x, y, width, height);
    } else {
      throw new Error(`Invalid rect dimensions or out of bounds: ${JSON.stringify(options.rect)}`);
    }
  }

  // Normalize target RGBA
  const [r, g, b, a] = rgbaToArray(target);
  const includeAlpha = options?.includeAlpha ?? false;

  const { width, height, data } = pixels;
  const totalPixels = width * height;
  let matching = 0;

  for (let i = 0; i < data.length; i += 4) {
    const rr = data[i];
    const gg = data[i + 1];
    const bb = data[i + 2];
    const aa = data[i + 3];
    const match =
      rr === r && gg === g && bb === b && (!includeAlpha || aa === (a ?? 255));
    if (match) matching++;
  }

  return matching / totalPixels;
}